import {useEffect, useState} from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import {AlertTriangle, ChevronLeft, ChevronRight, Users, X} from 'lucide-react'
import {
  AnalyticsAccessDetails,
  AnalyticsAccessTarget,
  AnalyticsFilters,
  getAnalyticsAccessDetails,
} from './analyticsApi'

const number = (value: number) => new Intl.NumberFormat('pt-BR').format(value)
const timeZone = 'America/Sao_Paulo'
const dateTime = (value: string | null, options: Intl.DateTimeFormatOptions) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('pt-BR', {...options, timeZone})
}

const AnalyticsAccessDetailsModal = ({
  target,
  filters,
  title,
  onClose,
}: {
  target: AnalyticsAccessTarget
  filters: AnalyticsFilters
  title: string
  onClose: () => void
}) => {
  const [details, setDetails] = useState<AnalyticsAccessDetails | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [returnFocus] = useState(() => document.activeElement as HTMLElement | null)
  const {from, to, user_guid, company_guid} = filters
  const {path, event_type, feature_name} = target

  useEffect(() => {
    const controller = new AbortController()
    let current = true
    setLoading(true)
    setError('')
    setDetails(null)
    getAnalyticsAccessDetails(
      {from, to, user_guid, company_guid},
      {path, event_type, feature_name},
      page,
      controller.signal
    ).then((result) => {
      if (current) setDetails(result)
    }).catch(() => {
      if (current) setError('Não foi possível carregar os detalhes de acesso. Tente novamente.')
    }).finally(() => {
      if (current) setLoading(false)
    })
    return () => {
      current = false
      controller.abort()
    }
  }, [from, to, user_guid, company_guid, path, event_type, feature_name, page, refreshKey])

  const pagination = details?.pagination
  const summary = details?.summary
  const accesses = details?.accesses || []
  const first = pagination && pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0
  const last = pagination ? Math.min(pagination.page * pagination.limit, pagination.total) : 0
  const periodDate = (value: string) => new Date(`${value}T12:00:00`).toLocaleDateString('pt-BR')

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className='analytics-feature-modal-backdrop analytics-access-backdrop'>
          <Dialog.Content className='analytics-feature-modal analytics-access-modal' onCloseAutoFocus={(event) => {
            event.preventDefault()
            if (returnFocus?.isConnected) returnFocus.focus()
          }}>
            <header>
              <div className='analytics-feature-modal__title'>
                <span><Users size={19} /></span>
                <div>
                  <small>Histórico de {event_type === 'feature_use' ? 'usos' : 'acessos'}</small>
                  <Dialog.Title asChild><h3>{title}</h3></Dialog.Title>
                  <Dialog.Description asChild>
                    <p>{path || 'Tela não identificada'} · {periodDate(from)} a {periodDate(to)} · Horário de Brasília</p>
                  </Dialog.Description>
                </div>
              </div>
              <Dialog.Close asChild><button type='button' aria-label='Fechar histórico de acessos'><X size={18} /></button></Dialog.Close>
            </header>

            {summary && (
              <div className='analytics-feature-modal__summary'>
                <div><span>{event_type === 'feature_use' ? 'Total de usos' : 'Total de acessos'}</span><strong>{number(summary.accesses)}</strong></div>
                <div><span>Usuários únicos</span><strong>{number(summary.users)}</strong></div>
                <div><span>Sessões</span><strong>{number(summary.sessions)}</strong></div>
                <div><span>Último {event_type === 'feature_use' ? 'uso' : 'acesso'}</span><strong className='analytics-access-modal__date'>{dateTime(summary.last_access, {dateStyle: 'short', timeStyle: 'medium'})}</strong></div>
              </div>
            )}

            <div className='analytics-feature-modal__content' aria-busy={loading}>
              {loading ? (
                <div className='analytics-loading' role='status'><span /><strong>Carregando histórico de acessos...</strong></div>
              ) : error ? (
                <div className='analytics-error' role='alert'>
                  <AlertTriangle size={22} /><p>{error}</p>
                  <button type='button' onClick={() => setRefreshKey((key) => key + 1)}>Tentar novamente</button>
                </div>
              ) : accesses.length ? (
                <>
                  <div className='analytics-feature-modal__section-title'>
                    <div><h4>Usuários e registros de acesso</h4><p>Cada linha representa uma ocorrência, da mais recente para a mais antiga.</p></div>
                    <span>{number(pagination?.total || 0)} registros</span>
                  </div>
                  <div className='analytics-feature-modal__table-wrap'>
                    <table>
                      <thead><tr><th>Usuário</th><th>Empresa</th><th>Data</th><th>Horário (Brasília)</th></tr></thead>
                      <tbody>
                        {accesses.map((access) => (
                          <tr key={access.id}>
                            <td><strong title={access.user_name}>{access.user_name}</strong><small className='analytics-access-modal__email'>{access.user_email || '-'}</small></td>
                            <td>{access.company_name || '-'}</td>
                            <td>{dateTime(access.occurred_at, {dateStyle: 'short'})}</td>
                            <td>{dateTime(access.occurred_at, {timeStyle: 'medium'})}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {pagination && (
                    <nav className='analytics-access-pagination' aria-label='Paginação do histórico de acessos'>
                      <span>{number(first)}–{number(last)} de {number(pagination.total)} registros</span>
                      <div>
                        <button type='button' onClick={() => setPage(pagination.page - 1)} disabled={pagination.page <= 1} aria-label='Página anterior de acessos'><ChevronLeft size={15} /> Anterior</button>
                        <span>Página {number(pagination.page)} de {number(pagination.total_pages)}</span>
                        <button type='button' onClick={() => setPage(pagination.page + 1)} disabled={pagination.page >= pagination.total_pages} aria-label='Próxima página de acessos'>Próxima <ChevronRight size={15} /></button>
                      </div>
                    </nav>
                  )}
                </>
              ) : (
                <div className='analytics-empty'><span><Users size={22} /></span><strong>Nenhum acesso encontrado</strong><p>Não há registros para esta tela no período e nos filtros selecionados.</p></div>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default AnalyticsAccessDetailsModal
